import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { productAPI, productFamilyAPI, productSubFamilyAPI } from "@/lib/api";

/**
 * Mini-formulaire modal de creation de produit depuis le picker.
 * Champs : nom (pre-rempli), reference (auto si vide), prix, TVA, famille (obligatoire).
 * A la validation, retourne le produit cree via onCreated(product).
 */
export default function InlineProductCreateModal({ open, initialName = "", onClose, onCreated }) {
  const [name, setName] = useState(initialName);
  const [reference, setReference] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [tvaRate, setTvaRate] = useState("20");
  const [familyId, setFamilyId] = useState("");
  const [subFamilyId, setSubFamilyId] = useState("");
  const [description, setDescription] = useState("");
  const [families, setFamilies] = useState([]);
  const [subFamilies, setSubFamilies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setReference("");
    setUnitPrice("");
    setTvaRate("20");
    setFamilyId("");
    setSubFamilyId("");
    setDescription("");
    productFamilyAPI.list().then(r => setFamilies(r.data || [])).catch(() => setFamilies([]));
    productSubFamilyAPI.list().then(r => setSubFamilies(r.data || [])).catch(() => setSubFamilies([]));
  }, [open, initialName]);

  const filteredSubs = familyId ? subFamilies.filter(s => s.family_id === familyId) : [];

  const parseFr = (v) => {
    if (v === "" || v == null) return 0;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Le nom est obligatoire"); return; }
    if (!familyId) { toast.error("Sélectionnez une famille"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        family_id: familyId,
        sub_family_id: subFamilyId || "",
        unit_price: parseFr(unitPrice),
        tva_rate: parseFr(tvaRate) || 20,
        active: true,
      };
      if (reference.trim()) payload.reference = reference.trim();
      const res = await productAPI.create(payload);
      toast.success(`Produit "${res.data.name}" créé`);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="inline-product-create-modal">
        <DialogHeader>
          <DialogTitle>Créer un produit</DialogTitle>
          <DialogDescription>Ajoute une fiche produit au catalogue. La référence est générée automatiquement si vide.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">Nom *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="inline-product-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase">Référence</Label>
              <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="auto" data-testid="inline-product-reference" />
            </div>
            <div>
              <Label className="text-xs uppercase">TVA %</Label>
              <Input value={tvaRate} onChange={e => setTvaRate(e.target.value)} data-testid="inline-product-tva" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase">Prix unit. HT</Label>
              <Input value={unitPrice} onChange={e => setUnitPrice(e.target.value)} placeholder="0,00" data-testid="inline-product-price" />
            </div>
            <div>
              <Label className="text-xs uppercase">Famille *</Label>
              <Select value={familyId} onValueChange={v => { setFamilyId(v); setSubFamilyId(""); }}>
                <SelectTrigger data-testid="inline-product-family"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {families.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {filteredSubs.length > 0 && (
            <div>
              <Label className="text-xs uppercase">Sous-famille</Label>
              <Select value={subFamilyId} onValueChange={setSubFamilyId}>
                <SelectTrigger data-testid="inline-product-subfamily"><SelectValue placeholder="Optionnel..." /></SelectTrigger>
                <SelectContent>
                  {filteredSubs.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase">Description</Label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Détail par défaut de la ligne (sauts de ligne préservés)"
              className="w-full min-h-[70px] text-sm rounded-md border border-slate-200 px-3 py-2 resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              data-testid="inline-product-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={onClose} disabled={saving} data-testid="inline-product-cancel">Annuler</Button>
          <Button type="button" onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="inline-product-submit">
            {saving ? "Création..." : "Créer le produit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
